import React, { useEffect, useRef, useState } from 'react';
import config from '../config/configuration_keys.json';

const SITE_KEY = config.REACT_APP_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = 'google-recaptcha-script';
const RECAPTCHA_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

const loadRecaptchaScript = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('Window is undefined'));
  if (window.grecaptcha) return Promise.resolve();
  if (window.__recaptchaScriptLoading) return window.__recaptchaScriptLoading;

  window.__recaptchaScriptLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = RECAPTCHA_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (error) => reject(new Error('Failed to load reCAPTCHA script: ' + error));
    document.body.appendChild(script);
  });

  return window.__recaptchaScriptLoading;
};

const RecaptchaCheckbox = ({ onChange, onError, value }) => {
  const widgetId = useRef(null);
  const containerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onErrorRef = useRef(onError);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    if (!SITE_KEY) {
      onErrorRef.current?.('Missing reCAPTCHA site key. Please add REACT_APP_RECAPTCHA_SITE_KEY to configuration_keys.json.');
      return;
    }

    let cancelled = false;

    const renderV2 = () => {
      if (!window.grecaptcha || typeof window.grecaptcha.render !== 'function') {
        onErrorRef.current?.('reCAPTCHA v2 failed to initialize. Please check your site key.');
        return;
      }

      if (containerRef.current && widgetId.current === null) {
        widgetId.current = window.grecaptcha.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => onChangeRef.current?.(token),
          'expired-callback': () => onChangeRef.current?.(null),
          'error-callback': () => onErrorRef.current?.('reCAPTCHA verification failed, please try again.'),
        });
        setIsReady(true);
      }
    };

    loadRecaptchaScript()
      .then(() => {
        if (cancelled) return;
        if (!window.grecaptcha) {
          onErrorRef.current?.('reCAPTCHA failed to initialize. Please refresh the page.');
          return;
        }

        if (typeof window.grecaptcha.ready === 'function') {
          window.grecaptcha.ready(renderV2);
        } else {
          renderV2();
        }
      })
      .catch((error) => {
        console.error(error);
        onErrorRef.current?.('Unable to load Google reCAPTCHA. Please refresh the page.');
      });

    return () => {
      cancelled = true;
      if (window.grecaptcha && widgetId.current !== null) {
        try {
          window.grecaptcha.reset(widgetId.current);
        } catch (e) {
          // ignore
        }
      }
    };
  }, []);

  useEffect(() => {
    if (value === null && window.grecaptcha && widgetId.current !== null) {
      try {
        window.grecaptcha.reset(widgetId.current);
      } catch (error) {
        console.error(error);
      }
    }
  }, [value]);

  return (
    <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
      <div ref={containerRef} />
      {!isReady && <p style={{ color: '#555', fontSize: '0.9rem' }}>Loading reCAPTCHA...</p>}
    </div>
  );
};

export default RecaptchaCheckbox;
